/** Query keys: one place, so invalidations and tests agree on every key. */
import type { AffordSource, Owner } from "../types";
import type { TxFilters } from "./transactions";

export const keys = {
  me: ["me"] as const,
  authConfig: ["auth-config"] as const,
  household: ["household"] as const,
  invitations: ["household", "invitations"] as const,
  adminHouseholds: ["household", "admin"] as const,
  dashboard: ["dashboard"] as const,
  accounts: ["accounts"] as const,
  cards: ["cards"] as const,
  // under "cards" so every money mutation refreshes it
  statements: (accountId: number) => ["cards", "statements", accountId] as const,
  reconciliation: ["cards", "reconciliation"] as const,
  bestCard: (amount: string, msi?: number, owner?: Owner) =>
    ["cards", "best", amount, msi, owner] as const,
  categories: ["categories"] as const,
  categoryRules: ["category-rules"] as const,
  periods: ["periods"] as const,
  transactions: (f: TxFilters) => ["transactions", f] as const,
  // Gastos' infinite list: filters stay at [1] so quick add can match them (`listShows`)
  transactionPages: (f: TxFilters) => ["transactions", f, "pages"] as const,
  budget: (period?: string) => ["budget", period ?? "current"] as const,
  budgetItems: ["budget-items"] as const,
  // under "payments" so every money mutation refreshes it (new charges, new fixed payments)
  recurring: ["payments", "recurring"] as const,
  // under "budget" so every money mutation refreshes it (spending moves the formula)
  categoryBudgets: ["budget", "categories"] as const,
  incomes: ["incomes"] as const,
  calendar: (start: string, end: string) => ["calendar", start, end] as const,
  byCategory: (period?: string, owner?: Owner) => ["charts", "category", period, owner] as const,
  byPeriod: (count: number) => ["charts", "period", count] as const,
  debtChart: ["charts", "debt"] as const,
  categoryTrend: (count: number) => ["charts", "category-trend", count] as const,
  // under "charts" so every money mutation refreshes it
  periodReport: (period?: string) => ["charts", "period-report", period ?? "last"] as const,
  debts: ["debts"] as const,
  pot: ["pot"] as const,
  potOverview: (period?: string) => ["pot", "overview", period ?? "current"] as const,
  potRepair: ["pot", "repair-deposits"] as const,
  savings: ["savings"] as const,
  imports: ["imports"] as const,
  import: (id: number) => ["imports", id] as const,
  // under the batch's key: anything that refreshes the batch refreshes its preview
  importPreview: (id: number) => ["imports", id, "preview"] as const,
  importUndo: (id: number) => ["import-undo", id] as const,
  verifyOptions: ["import-verify-options"] as const,
  // under "imports": saving or undoing an import refreshes it
  importMetrics: ["imports", "metrics"] as const,
  sheetImports: ["imports", "sheet"] as const,
  // under "transactions" so every money mutation refreshes it
  sheetCleanSlate: ["transactions", "sheet-count"] as const,
  affordability: (
    price: string,
    msi?: number,
    source?: AffordSource,
    categoryId?: number,
    accountId?: number,
  ) => ["affordability", price, msi, source, categoryId, accountId] as const,
  settings: ["settings"] as const,
  notifications: ["notification-settings"] as const,
  payments: (period?: string) => ["payments", period ?? "current"] as const,
  paymentsYear: (year: number) => ["payments", "year", year] as const,
  plan: (period?: string) => ["plan", period ?? "current"] as const,
  planPeriods: ["periods", "plan"] as const,
  planAllocations: ["plan-allocations"] as const,
};
